import { type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Clock } from '@/components/icons';
import { useTenant } from '@/contexts/TenantContext';
import { usePermissions } from '@/permissions/use-permissions';
import { useActivePartner } from '@/lib/api/partners';
import { useTimeTravelOverride } from '@/features/admin/time-travel/api/use-time-travel';

/**
 * `TimeTravelBanner` (#1550 global; #1632 scope-aware).
 *
 * Always-visible strip rendered when a wall-clock override applies to the
 * viewer. Mounted in the app shell so every authenticated user sees the
 * simulated date the moment an admin flips the toggle — surprise dates are
 * worse than visible ones.
 *
 * #1632 — overrides resolve per-org → global (mirroring `ClockService`), so
 * an org-scoped override for the viewer's active org takes precedence over a
 * global one and the copy names the org. The per-org read is role-gated
 * server-side, so we only fire it when the viewer can read org overrides
 * (`admin.timeTravel.org` / `read`); otherwise the banner reflects the
 * global override alone. Renders nothing when nothing is active or a read
 * fails (best-effort UX, never blocking).
 */
export function TimeTravelBanner(): JSX.Element | null {
  const { t } = useTranslation();
  const { activePartnerId } = useTenant();
  const { can } = usePermissions();
  const { active: activeOrg } = useActivePartner();

  const canReadOrg = can('admin.timeTravel.org', 'read');
  const globalOverride = useTimeTravelOverride();
  const orgOverride = useTimeTravelOverride({
    scope: 'org',
    organizationId: canReadOrg ? activePartnerId : null,
  });

  const orgRow = orgOverride.data;
  const globalRow = globalOverride.data;

  if (orgRow?.active && orgRow.customDate) {
    return (
      <BannerStrip>
        {t('timeTravelBanner.orgMessage', {
          org: activeOrg?.name ?? '',
          date: orgRow.customDate,
        })}
      </BannerStrip>
    );
  }

  if (globalRow?.active && globalRow.customDate) {
    return (
      <BannerStrip>
        {t('timeTravelBanner.message', { date: globalRow.customDate })}
      </BannerStrip>
    );
  }

  return null;
}

function BannerStrip({ children }: { children: ReactNode }): JSX.Element {
  return (
    <div
      role="status"
      data-testid="time-travel-banner"
      className="flex items-center justify-center gap-2 border-b border-amber-300 bg-amber-100 px-4 py-2 text-sm font-medium text-amber-900 dark:border-amber-700 dark:bg-amber-950 dark:text-amber-100"
    >
      <Clock className="h-4 w-4" aria-hidden="true" />
      <span>{children}</span>
    </div>
  );
}
